import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import i18n from '../../../utils/i18n';
import Button from '@adsk/bim360-matrix-react-button';
import InputValidationView from '@adsk/bim360-matrix-react-input-validation-view';
import PermissionSelector from '@adsk/bim360-matrix-react-permission-selector';
import {NotificationLevels, NotificationTypes} from '@adsk/bim360-matrix-react-notification-manager/es/helpers/consts';
import TryAgainErrorNotification from '../../common/TryAgainErrorNotification';
import {getUserCompanyName} from '../../../utils/userHelpers';
import {issueAddPermission} from '../../../utils/analytics-helpers';
import {
  AdminPermissionBulkOperations,
  AdminPermissionsV2TypeToActions,
  BULK_CREATE_PERMISSION_COUNT_LIMIT_DEFAULT,
  NewAdminPermissionsArray,
  PermissionsLevelsToIds
} from '../../../constants/issues-admin';
import {HQ_COMPANY_TYPE, HQ_ROLE_TYPE, HQ_USER_TYPE} from '../../../constants/hq-types';
import lodashCountBy from 'lodash/countBy';
import SelectBoxAdapter from '../../common/SelectBoxAdapter';
import {ENV_CONFIGS} from '../../../constants/envs';

class IssuesPermissionsBulkSelection extends PureComponent {
  constructor(props) {
    super(props);

    let viewPermission = null;
    this.adminPermissionsArray = NewAdminPermissionsArray.map(permission => {
      const permissionOption = Object.assign({}, permission, {
        label: i18n.t(`issuesAdmin.permissions.permissionsType.${permission.label}`)
      });
      if (permissionOption.id === PermissionsLevelsToIds.view) {
        viewPermission = permissionOption;
      }
      return permissionOption;
    });

    this.state = {
      selectedEntities: [],
      selectedPermission: viewPermission,
      invalidEntry: false,
      maxExceeded: false
    };

    this.handleEntitiesChange = this.handleEntitiesChange.bind(this);
    this.handleDropdownClose = this.handleDropdownClose.bind(this);
    this.handlePermissionChange = this.handlePermissionChange.bind(this);
    this.handleAddEntry = this.handleAddEntry.bind(this);
    this.onSelectComponentOpen = this.onSelectComponentOpen.bind(this);
  }

  handleEntitiesChange(selectedEntities) {
    if (selectedEntities === null) {
      selectedEntities = [];
    }
    const bulkCreatePermissionCountLimit =
      ENV_CONFIGS.BULK_CREATE_PERMISSION_COUNT_LIMIT ?? BULK_CREATE_PERMISSION_COUNT_LIMIT_DEFAULT;
    if (selectedEntities.length <= bulkCreatePermissionCountLimit) {
      this.setState({selectedEntities, maxExceeded: false});
    } else {
      const prevSelectedEntities = this.state.selectedEntities;
      this.setState({selectedEntities, maxExceeded: true}, () => {
        this.setState({selectedEntities: prevSelectedEntities});
      });
    }
  }

  handleDropdownClose() {
    this.setState({maxExceeded: false});
  }

  handlePermissionChange(selectedPermissionId) {
    const selectedPermission = this.adminPermissionsArray.find(permission => permission.id === selectedPermissionId);
    this.setState({selectedPermission});
  }

  handleBulkSuccess = ({successPermissions, failedPermissions}) => {
    if (successPermissions.length) {
      this.props.onSuccess(successPermissions);

      const typeToCount = lodashCountBy(successPermissions, 'subjectType');

      this.props.trackEvent(
        issueAddPermission(
          typeToCount[HQ_USER_TYPE],
          typeToCount[HQ_COMPANY_TYPE],
          typeToCount[HQ_ROLE_TYPE],
          this.state.selectedPermission.v2Value
        )
      );
    }
    if (failedPermissions.length) {
      this.showErrorNotification(failedPermissions);
      const failedSelectedEntities = failedPermissions.map(permission =>
        this.state.selectedEntities.find(
          e => e.oxygenType === permission.subjectType && e.oxygenId === permission.subject
        )
      );
      this.setState({selectedEntities: failedSelectedEntities});
    } else {
      this.setState({selectedEntities: []});
    }
  };

  handleBulkError = failedPermissions => {
    this.showErrorNotification(failedPermissions.map(permission => permission.resource));
  };

  showErrorNotification = failedPermissions => {
    const typeToCount = {
      [HQ_USER_TYPE]: 0,
      [HQ_COMPANY_TYPE]: 0,
      [HQ_ROLE_TYPE]: 0
    };
    const typeToTranslationKey = {
      [HQ_USER_TYPE]: 'oxygenEntitiesCount.users',
      [HQ_COMPANY_TYPE]: 'oxygenEntitiesCount.companies',
      [HQ_ROLE_TYPE]: 'oxygenEntitiesCount.roles'
    };
    failedPermissions.forEach(permission => {
      typeToCount[permission.subjectType]++;
    });
    const typesArr = [HQ_USER_TYPE, HQ_ROLE_TYPE, HQ_COMPANY_TYPE]
      .filter(oxygenType => typeToCount[oxygenType])
      .map(oxygenType => {
        const count = typeToCount[oxygenType];
        const baseTranslationKey = typeToTranslationKey[oxygenType];
        const translationKey = count === 1 ? `${baseTranslationKey}.single` : `${baseTranslationKey}.plural`;
        return i18n.t(translationKey, {count});
      });

    let translationKey, translationParams;
    if (typesArr.length === 1) {
      translationKey = 'issuesAdmin.permissions.notifications.bulkAddFailedSingleType';
      translationParams = {type: typesArr[0]};
    } else {
      translationKey = 'issuesAdmin.permissions.notifications.bulkAddFailedMultiType';
      translationParams = {firstElements: typesArr.slice(0, -1).join(', '), lastElement: typesArr[typesArr.length - 1]};
    }

    this.props.showNotificationAction({
      type: NotificationTypes.SNACKBAR,
      level: NotificationLevels.ERROR,
      message: (
        <TryAgainErrorNotification
          message={i18n.t(translationKey, translationParams)}
          tryAgainAction={this.handleAddEntry}
        />
      )
    });
  };

  handleAddEntry() {
    const entities = this.state.selectedEntities;

    if (!entities.length) {
      this.setState({invalidEntry: true});
    } else {
      const bulkOperations = this.state.selectedEntities.map(entity => ({
        correlationId: entity.oxygenId,
        op: AdminPermissionBulkOperations.CREATE,
        resource: {
          subject: entity.oxygenId,
          subjectType: entity.oxygenType,
          actions: AdminPermissionsV2TypeToActions[this.state.selectedPermission.v2Value]
        }
      }));

      const onError = () => this.handleBulkError(bulkOperations);
      const permissionsData = {operations: bulkOperations};
      this.props.createBulkIssuesPermissions(permissionsData, this.handleBulkSuccess, onError);
    }
  }

  onSelectComponentOpen() {
    if (this.state.invalidEntry) {
      this.setState({invalidEntry: false});
    }
  }

  renderSelectComponent() {
    return (
      <SelectBoxAdapter
        grouped
        searchable
        multi
        clearable
        valid={!this.state.maxExceeded}
        className={classNames('IssuesPermissionsBulkSelection__search-bar', {
          'IssuesPermissionsBulkSelection__search-bar--invalid': this.state.invalidEntry
        })}
        labelKey="name"
        valueKey="oxygenId"
        dropdownMaxWidth={398}
        subtextGetter={getUserCompanyName}
        placeholder={i18n.t('issuesAdmin.permissions.searchPlaceholder')}
        options={this.props.permissionsCandidatesEntities}
        selectedOptions={this.state.selectedEntities}
        onChange={this.handleEntitiesChange}
        onOpen={this.onSelectComponentOpen}
        onClose={this.handleDropdownClose}
        noResultsValue={this.props.permissionsCandidatesEntities.length === 0
          ? i18n.t('issuesAdmin.permissions.searchPlaceholder')
          : undefined}
      />
    );
  }

  render() {
    const disabled = this.props.isBulkCreatingPermissions || this.props.isFetchingPermissions;
    return (
      <div className="IssuesPermissionsBulkSelection">
        <div
          className={classNames('IssuesPermissionsBulkSelection__search-bar-container', {
            'IssuesPermissionsBulkSelection__search-bar-container--invalid': this.state.invalidEntry
          })}
        >
          <InputValidationView
            message={this.state.maxExceeded
              ? i18n.t('issuesAdmin.permissions.selectedLimitError', {
                limit: ENV_CONFIGS.BULK_CREATE_PERMISSION_COUNT_LIMIT ?? BULK_CREATE_PERMISSION_COUNT_LIMIT_DEFAULT
              })
              : ''}
            type={InputValidationView.Types.POPOVER}
            popoverContainer={this}
          >
            {this.renderSelectComponent()}
          </InputValidationView>
          <PermissionSelector
            permissions={this.adminPermissionsArray}
            selectedPermissionId={this.state.selectedPermission.id}
            onClick={this.handlePermissionChange}
            onLearnMore={this.props.onLearnMore}
            canToggle={!disabled}
          />
          <Button
            className="IssuesPermissionsBulkSelection__add"
            data-testid="IssuesPermissionsBulkSelection__add"
            styleType={Button.StyleType.PRIMARY}
            onClick={this.handleAddEntry}
            disabled={disabled}
          >
            {this.props.isBulkCreatingPermissions
              ? i18n.t('issuesAdmin.permissions.addPermissionButtonIsAdding')
              : i18n.t('issuesAdmin.permissions.addPermissionButton')}
          </Button>
        </div>
        {this.state.invalidEntry && (
          <div className="IssuesPermissionsBulkSelection__invalid-entry-message">
            {i18n.t('issuesAdmin.permissions.addPermissionsInvalidEntry')}
          </div>
        )}
      </div>
    );
  }
}

IssuesPermissionsBulkSelection.propTypes = {
  onLearnMore: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,

  // From store
  isFetchingPermissions: PropTypes.bool.isRequired,
  isBulkCreatingPermissions: PropTypes.bool.isRequired,
  permissionsCandidatesEntities: PropTypes.array,

  // Dispatch
  createBulkIssuesPermissions: PropTypes.func.isRequired,
  showNotificationAction: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired
};

IssuesPermissionsBulkSelection.displayName = 'IssuesPermissionsBulkSelection';

export default IssuesPermissionsBulkSelection;
